import api from '../api/config';
import type { ChatActivity, ChatDoc, ChatFeed, ChatType } from '@odo/shared-ui';
import { categorizeAsset } from '@odo/shared-ui';
import { uploadThumbnail } from './patientFiles';

export type { ChatActivity, ChatAsset, ChatMessage, ChatFeed, ChatType } from '@odo/shared-ui';

// Single source of truth for the chatType discriminator: sent only for the
// internal thread, omitted for external (the server default).
const chatTypeParam = (chatType?: ChatType): { chatType: ChatType } | undefined =>
    chatType === 'internal' ? { chatType } : undefined;

export const fetchPatientChat = async (id: string, chatType?: ChatType): Promise<ChatFeed> => {
    const response = await api.get(`/patients/${id}/chat`, { params: chatTypeParam(chatType) });
    if (response.status !== 200) {
        return { itemId: id, messages: [] };
    }
    return response.data;
};

export const fetchPatientChatActivities = async (
    id: string,
    chatType?: ChatType
): Promise<ChatActivity[]> => {
    const response = await api.get(`/patients/${id}/chat/activities`, {
        params: chatTypeParam(chatType)
    });
    if (response.status !== 200) return [];
    return response.data?.activities ?? [];
};

export const sendChatMessage = async (
    id: string,
    message: string,
    chatType?: ChatType,
    bodyDoc?: ChatDoc
): Promise<{ updateId: number } | null> => {
    const response = await api.post(`/patients/${id}/chat/message`, {
        message,
        bodyDoc,
        ...chatTypeParam(chatType)
    });
    return response.data;
};

export const editChatMessage = async (
    patientId: string,
    updateId: string,
    message: string,
    bodyDoc?: ChatDoc
): Promise<{ updateId: number }> => {
    const response = await api.post(
        `/patients/${patientId}/chat/message/${updateId}`,
        { message, bodyDoc },
        { keepPageOnForbidden: true }
    );
    return response.data;
};

export const deleteChatMessage = async (
    patientId: string,
    updateId: string
): Promise<{ updateId: number }> => {
    const response = await api.delete(`/patients/${patientId}/chat/message/${updateId}`, {
        keepPageOnForbidden: true
    });
    return response.data;
};

export const removeChatAttachment = async (
    patientId: string,
    updateId: string,
    patientFileId: string
): Promise<void> => {
    await api.delete(`/patients/${patientId}/chat/message/${updateId}/files/${patientFileId}`);
};

interface CreateUploadResponse {
    fileId: string;
    patientFileId: string;
    objectPath: string;
    uploadUrl: string;
    headers: Record<string, string>;
}

export interface ChatStatus {
    hasUpdate: boolean;
    otherSideLastSeenAt: string | null;
    /** Team + assignment of the viewer row this thread reads, ODO side. */
    team: string | null;
    teamRoleId: string | null;
    assignedTo: string | null;
    reviewedBy: string | null;
}

export const fetchChatStatus = async (id: string, chatType?: ChatType): Promise<ChatStatus> => {
    const response = await api.get(`/patients/${id}/chat-status`, {
        params: chatTypeParam(chatType)
    });
    return response.data;
};

export const markChatAsSeen = async (id: string, chatType?: ChatType): Promise<void> => {
    await api.post(`/patients/${id}/chat/seen`, chatTypeParam(chatType) ?? {});
};

export const markChatAsUnread = async (id: string, chatType?: ChatType): Promise<void> => {
    await api.post(`/patients/${id}/chat/unread`, chatTypeParam(chatType) ?? {});
};

export const setChatTeam = async (id: string, team: string, chatType?: ChatType): Promise<void> => {
    await api.post(`/patients/${id}/chat/team`, { team, ...chatTypeParam(chatType) });
};

export const assignChatUser = async (
    patientId: string,
    email: string,
    expectedAssignedTo: string | null,
    chatType?: ChatType
): Promise<void> => {
    await api.post(`/patients/${patientId}/chat/assign`, {
        email,
        expectedAssignedTo,
        ...chatTypeParam(chatType)
    });
};

export const unassignChatUser = async (
    patientId: string,
    expectedAssignedTo: string | null,
    chatType?: ChatType
): Promise<void> => {
    await api.post(`/patients/${patientId}/chat/unassign`, {
        expectedAssignedTo,
        ...chatTypeParam(chatType)
    });
};

export const uploadChatFile = async (
    patientId: string,
    file: File,
    updateId: string | number | undefined,
    onProgress?: (progress: number) => void,
    chatType?: ChatType
): Promise<{ fileId: string }> => {
    const createResponse = await api.post<CreateUploadResponse>(
        `/patients/${patientId}/cloud-files/upload`,
        {
            fileType: 'CHAT_ATTACHMENT',
            originalFilename: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            ...(updateId !== undefined && { metadata: { updateId: String(updateId) } }),
            ...(chatType === 'internal' && { internal: true })
        }
    );
    const { uploadUrl, headers, patientFileId, fileId } = createResponse.data;

    await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress((e.loaded / e.total) * 100);
            }
        });
        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        Object.entries({
            ...headers,
            'Content-Type': file.type || 'application/octet-stream'
        }).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        xhr.send(file);
    });

    await api.post(`/patients/${patientId}/cloud-files/${patientFileId}/complete`);

    // Best-effort thumbnail for image attachments (parity with patient-file
    // uploads). Awaited but non-fatal — the attachment is already uploaded.
    if (categorizeAsset(file.name) === 'Images') {
        try {
            await uploadThumbnail(patientId, fileId, file);
        } catch (err) {
            console.warn('Chat attachment thumbnail failed (non-fatal):', err);
        }
    }
    return { fileId };
};
